import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { EnterpriseAppState, TodoItemEntity, Enterprise as App, GamePlans, TodoItemStatus } from "@ctra/api";
import { isFulfilled, isRejected, Nullable } from "@ctra/utils";
import { Enterprise, useTranslation } from "@ctra/i18n";
import { message } from "@ctra/components";

/**
 * hook that manages the updation cycle of a task
 * displays relevant messages based on async
 * @returns updatingTasks & setUpdationTask
 */
export const useTaskUpdateCycle = (): {
  updatingTasks: Array<TodoItemEntity["id"]>;
  setUpdatingTask: Dispatch<SetStateAction<Nullable<TodoItemEntity>>>;
} => {
  const [updatingTask, setUpdatingTask] = useState<Nullable<TodoItemEntity>>(null);
  const { t } = useTranslation();
  const {
    gamePlan: {
      todo: { update }
    }
  } = Enterprise;

  /**
   * Get the tasks which are currently being updated
   */
  const updatingTasks = useSelector<EnterpriseAppState, Array<TodoItemEntity["id"]>>(
    App.entities.getUpdatingTasks
  );

  /**
   * Check if the task updation is complete
   */
  const isTaskUpdated = useSelector<EnterpriseAppState, boolean>((state) =>
    updatingTask ? isFulfilled(state, GamePlans.types.UPDATE_TASK, { primaryValue: updatingTask.id }) : false
  );

  /**
   * Check if the task updation is complete
   */
  const isTaskUpdationFailed = useSelector<EnterpriseAppState, boolean>((state) =>
    updatingTask ? isRejected(state, GamePlans.types.UPDATE_TASK, { primaryValue: updatingTask.id }) : false
  );

  /**
   * If a task updation is successful, show relevant message
   */
  useEffect(() => {
    if (isTaskUpdated && updatingTask) {
      updatingTask.status === TodoItemStatus.ToDo
        ? message.success(t<string>(update.complete))
        : message.info(t<string>(update.unmark));
      setUpdatingTask(null);
    }
    if (isTaskUpdationFailed) {
      message.error(t<string>(update.reject));
    }
  }, [isTaskUpdated, updatingTask, isTaskUpdationFailed, t, update]);

  return { updatingTasks, setUpdatingTask };
};
